/**
 * Drag handle between docked panels.
 *
 * Pointer capture rather than window-level mousemove: the drag must keep
 * tracking when the cursor crosses the WebGL viewport, which swallows plain
 * mouse events on some platforms.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

export interface SplitterProps {
  orientation: "vertical" | "horizontal";
  /** Current size, in px, of the panel being resized. */
  size: number;
  /** Sign of the delta: "start" grows with the pointer, "end" shrinks. */
  edge: "start" | "end";
  min?: number;
  max?: number;
  onResize: (size: number) => void;
  onDoubleClick?: () => void;
}

export function Splitter({
  orientation,
  size,
  edge,
  min = 140,
  max = 720,
  onResize,
  onDoubleClick,
}: SplitterProps) {
  const start = React.useRef({ pos: 0, size: 0 });
  const [dragging, setDragging] = React.useState(false);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { pos: orientation === "vertical" ? e.clientX : e.clientY, size };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const pos = orientation === "vertical" ? e.clientX : e.clientY;
    const delta = (pos - start.current.pos) * (edge === "start" ? 1 : -1);
    onResize(Math.max(min, Math.min(max, start.current.size + delta)));
  };

  const stop = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
  };

  return (
    <div
      role="separator"
      aria-orientation={orientation}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onDoubleClick={onDoubleClick}
      className={cn(
        "shrink-0 bg-ads-border transition-colors duration-100 hover:bg-ads-accent/60",
        dragging && "bg-ads-accent",
        orientation === "vertical" ? "w-px cursor-col-resize" : "h-px cursor-row-resize",
        // A 1px hit target is unusable; widen it without moving the pixel line.
        orientation === "vertical"
          ? "relative after:absolute after:inset-y-0 after:-left-1 after:w-[9px] after:content-['']"
          : "relative after:absolute after:inset-x-0 after:-top-1 after:h-[9px] after:content-['']",
      )}
    />
  );
}
